import React, {useEffect, useMemo, useState} from 'react';
import {App, Spin} from 'antd';
import {useCompositeSignals} from './hooks/useCompositeSignals';
import {CompositeSignalCard} from './components/CompositeSignalCard';
import {generateCompositeSignals} from '../../../services/compositeSignalService';
import type {StatusFilter} from '../../../types/compositeSignal';
// 复用 pattern 页的变量体系与基础控件样式（工具栏/选择器/按钮组），保证两页外观一致
import '../PatternRecognitionPage.css';
import './CompositeSignalPage.css';

const INTERVALS = ['5m', '1H', '4H', '1D'] as const;
const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
    {key: 'ACTIVE', label: '生效中'},
    {key: 'EXPIRED', label: '已过期'},
    {key: 'ALL', label: '全部'},
];
/**
 * 与 pattern 页同源的兜底品种列表（topN 加载失败时使用）
 */
const TOP_COINS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'DOT'];
const DEFAULT_SYMBOLS = TOP_COINS.map(c => `${c}-USDT-SWAP`);

function symbolDisplay(s: string): string {
    return s.replace('-USDT-SWAP', '/USDT').replace('-USDT', '/USDT');
}

interface CompositeSignalPageProps {
    initialSymbol?: string;
    initialInterval?: string;
}

export const CompositeSignalPage: React.FC<CompositeSignalPageProps> = ({
                                                                            initialSymbol = 'BTC-USDT-SWAP',
                                                                            initialInterval = '1H',
                                                                        }) => {
    const {
        symbol, setSymbol,
        interval, setIntervalState,
        statusFilter, setStatusFilter,
        autoRefresh, setAutoRefresh,
        signals, loading, error, lastUpdated,
        apiKeyId,
        refresh, reload,
    } = useCompositeSignals({symbol: initialSymbol, interval: initialInterval});

    const {message} = App.useApp();

    const [symbols] = useState<string[]>(DEFAULT_SYMBOLS);
    const [generating, setGenerating] = useState(false);
    // 展开证据行的信号 id（K线图由各卡片自行管理，支持多 Item 同时展开互不共享）
    const [expandedId, setExpandedId] = useState<number | null>(null);

    // 切换品种/时间帧/状态过滤时，关闭展开的证据行（信号集合整体替换，K线图随卡片重建清零）
    useEffect(() => {
        setExpandedId(null);
    }, [symbol, interval, statusFilter]);

    const summary = useMemo(() => {
        const active = signals.filter(s => 'ACTIVE' === s.status);
        return {
            total: active.length,
            bull: active.filter(s => 1 === s.direction).length,
            bear: active.filter(s => -1 === s.direction).length,
            neutral: active.filter(s => 0 === s.direction).length,
        };
    }, [signals]);

    const handleGenerate = async () => {
        if (generating) return;
        setGenerating(true);
        try {
            const count = await generateCompositeSignals(symbol, interval);
            message.success(`本次合成 ${count ?? 0} 条`);
            reload();
        } catch {
            message.error('生成失败，请稍后重试');
        } finally {
            setGenerating(false);
        }
    };

    const updatedText = null == lastUpdated ? '' : new Date(lastUpdated).toLocaleTimeString('zh-CN', {hour12: false});

    return (
        <div className="pattern-page cs-page">
            <div className="chart-toolbar">
                <select className="pattern-select" defaultValue="OKX" aria-label="交易所">
                    <option value="OKX">OKX</option>
                    <option value="Bybit" disabled>Bybit</option>
                    <option value="Hyperliquid" disabled>Hyperliquid</option>
                </select>
                <select
                    className="pattern-select"
                    value={symbol}
                    onChange={e => setSymbol(e.target.value)}
                    aria-label="品种"
                >
                    {symbols.map(s => <option key={s} value={s}>{symbolDisplay(s)}</option>)}
                </select>
                <div className="toolbar-sep"/>
                <div className="filterbar">
                    {INTERVALS.map(iv => (
                        <button
                            key={iv}
                            className={`filterbar-btn${interval === iv ? ' active' : ''}`}
                            onClick={() => setIntervalState(iv)}
                        >
                            {iv}
                        </button>
                    ))}
                </div>
                <div className="toolbar-sep"/>
                <div className="filterbar">
                    {STATUS_FILTERS.map(f => (
                        <button
                            key={f.key}
                            className={`filterbar-btn${statusFilter === f.key ? ' active' : ''}`}
                            onClick={() => setStatusFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="toolbar-sep"/>
                <button className="cs-generate-btn" disabled={generating} onClick={handleGenerate}>
                    {generating ? '合成中…' : '⟳ 生成'}
                </button>
                <label className="cs-auto-refresh">
                    <input
                        type="checkbox"
                        checked={autoRefresh}
                        onChange={e => setAutoRefresh(e.target.checked)}
                    />
                    自动刷新
                </label>
            </div>

            <div className="cs-summary-bar">
                <span>
                    生效 <b className="cs-num">{summary.total}</b> 条 ·
                    <b className="cs-num bull">{summary.bull}</b> 看多 ·
                    <b className="cs-num bear">{summary.bear}</b> 看空 ·
                    <b className="cs-num neutral">{summary.neutral}</b> 中性
                </span>
                {updatedText && <span className="cs-updated-at">数据更新于 {updatedText}</span>}
            </div>

            <div className="cs-list">
                {loading && !signals.length ? (
                    <div className="cs-loading"><Spin/></div>
                ) : error && !signals.length ? (
                    <div className="cs-empty">
                        <div className="cs-empty-title">加载失败</div>
                        <div className="cs-empty-desc">{error}</div>
                        <button className="cs-action-btn" onClick={refresh}>重试</button>
                    </div>
                ) : !signals.length ? (
                    <div className="cs-empty">
                        <div className="cs-empty-icon">◎</div>
                        <div className="cs-empty-title">当前条件下暂无组合信号</div>
                        <div className="cs-empty-desc">点位形态与支撑/阻力区域暂未位置共振；可点击「生成」立即聚合一次。
                        </div>
                        <button className="cs-action-btn" onClick={handleGenerate}>立即生成</button>
                    </div>
                ) : (
                    signals.map(s => (
                        <CompositeSignalCard
                            key={s.id}
                            signal={s}
                            expanded={expandedId === s.id}
                            onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
                            apiKeyId={apiKeyId ?? undefined}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export default CompositeSignalPage;
